// src/lib/simulationApi.js
import { callAi } from "./geminiClient";

/** ───────── 공통 유틸 ───────── */
function getUserOrDie() {
  const userStr =
    (typeof localStorage !== "undefined" && localStorage.getItem("user")) || "";
  const user = userStr ? JSON.parse(userStr) : null;
  if (!user) {
    alert("로그인 후 사용해 주세요");
    if (typeof window !== "undefined") window.location.href = "/login";
    throw new Error("NOT_AUTHENTICATED");
  }
  return user;
}
function getLang() {
  const lang =
    (typeof document !== "undefined" &&
      document.documentElement &&
      document.documentElement.lang) ||
    "ko";
  return lang === "zh-CN" ? "zh-CN" : "ko";
}

/** ───────── 프롬프트 빌더 ───────── */
export function buildInitPrompt(lang = getLang()) {
  if (lang === "zh-CN") {
    return `你是“${process.env.REACT_APP_CN_CLINIC_NAME}”的【不满意患者】角色模拟机器人。
患者档案：朴智惠，32岁，近两周服用减肥处方中药。诉说心悸、手抖、焦虑等不适，对疗程存在不满。
目的：训练前台/呼叫中心员工的应对能力。
规则：
- 你只扮演“患者”。用真实的口吻表达不满/疑问/诉求。
- 每次只用1~2句话，并等待工作人员下一轮发言。
- 不进行过度医学解释或下定论。
请生成【开场第一句话】（仅1~2句）。`;
  }
  return `당신은 '${process.env.REACT_APP_CLINIC_NAME}'의 불만 환자 역할을 맡은 시뮬레이션 봇입니다.
환자 프로필: 박지혜(32), 2주간 한약 다이어트 복용 후 불편감 호소.
규칙:
- 환자 역할만 수행, 1~2문장으로 반응 후 대기.
- 과도한 의학 설명/단정 금지.
시작 멘트를 1~2문장으로 생성하세요.`;
}

export function buildReplyPrompt(chatHistory, lang = getLang()) {
  const transcript = chatHistory
    .map((m) =>
      m.role === "user"
        ? (lang === "zh-CN" ? `工作人员: ${m.text}` : `스태프: ${m.text}`)
        : (lang === "zh-CN" ? `患者: ${m.text}` : `환자: ${m.text}`)
    )
    .join("\n");

  if (lang === "zh-CN") {
    return `下面是不满意患者应对模拟的对话日志。
你扮演【患者】。承接工作人员最后一句，用患者口吻自然回应【仅1~2句】。

[对话日志]
${transcript}`;
  }
  return `아래는 불만 환자 응대 시뮬레이션 대화입니다.
당신은 '환자'입니다. 스태프의 마지막 멘트에 이어 1~2문장으로 자연스럽게 반응하세요.

[대화 로그]
${transcript}`;
}

function buildCoachingPrompt(lang, staffUtterance, patientUtterance) {
  if (lang === "zh-CN") {
    return (
`你是“${process.env.REACT_APP_CN_CLINIC_NAME}”的通话教练。请仅给出对【员工】刚才话术的一条简短改进建议。
- 结合【患者】的上一句发言，指出员工话术中最关键的一个改进点
- 用礼貌、专业的中文
- 长度严格限制：一段、最多两句、120字以内
- 禁止：项目符号、列表、表格、标题、Emoji、寒暄模板
- 必须包含可立即说出的替代句子 1个（用引号标出）

【患者】：「${patientUtterance}」
【员工】：「${staffUtterance}」

现在直接输出改进建议一段话。`
    );
  }

  // 한국어 기본
  return (
`당신은 '${process.env.REACT_APP_CLINIC_NAME}'의 통화 코치입니다. 방금 【스태프】 멘트에 대해
가장 중요한 개선점 1가지만 간결히 제시하세요.
- 【환자】의 직전 발화를 고려해 핵심 개선 포인트 1개
- 정중하고 전문적인 한국어
- 길이 제한: 한 문단, 최대 2문장, 120자 이내
- 금지: 불릿/리스트/표/제목/이모지/장황한 서론
- 반드시 바로 말할 수 있는 대체 문장 1개 포함(따옴표로 표기)

【환자】：「${patientUtterance}」
【스태프】：「${staffUtterance}」

지금 바로 한 문단으로만 출력하세요.`
  );
}

/** ✅ 추천 멘트: 순수 JSON만 (강건 파싱 포함) */
export function buildRecommendationPrompt(lastPatientUtterance, lang = getLang()) {
  if (lang === "zh-CN") {
    return `仅以**纯JSON**输出，不要包含任何\`\`\`或文字说明。
针对下述患者发言，生成工作人员可用的三种策略回复：
患者发言："""${lastPatientUtterance}"""

严格输出此JSON : 
{"options":[
  {"strategy":"共情","text":"..."},
  {"strategy":"信息收集","text":"..."},
  {"strategy":"解决","text":"..."}
]}`;
  }
  return `**반드시 순수 JSON만** 출력하세요. \`\`\` 같은 코드펜스나 설명 문구 금지.
아래 환자 멘트에 대응하는 스태프용 추천 멘트를 3가지 전략으로 생성:
환자 멘트: """${lastPatientUtterance}"""

정확한 JSON 스키마:
{"options":[
  {"strategy":"공감","text":"..."},
  {"strategy":"정보수집","text":"..."},
  {"strategy":"해결","text":"..."}
]}`;
}

export function buildEvaluationPrompt(chatHistory, lang = getLang()) {
  const transcript = chatHistory
    .map((m) =>
      m.role === "user"
        ? (lang === "zh-CN" ? `工作人员: ${m.text}` : `스태프: ${m.text}`)
        : (lang === "zh-CN" ? `患者: ${m.text}` : `환자: ${m.text}`)
    )
    .join("\n");
  if (lang === "zh-CN") {
    return `以下是整个模拟对话：
${transcript}

请用中文输出最终评估：
### 分数（100分满分）
### 总结（2~3句）
### 优势（3条）
- 
- 
- 
### 改进点（3条）
- 
- 
- 
### 下一次通话建议（3条）
- 
- 
- `;
  }
  return `다음은 전체 시뮬레이션 대화입니다:
${transcript}

최종 평가를 아래 형식으로 작성하세요.
### 점수(100점 만점)
### 요약(2~3줄)
### 강점(3가지)
- 
- 
- 
### 개선 포인트(3가지)
- 
- 
- 
### 다음 통화를 위한 코칭 팁(3가지)
- 
- 
- `;
}

/** (누락 보완) SMS/메모 빌더 */
export function buildSmsPrompt(chatHistory, lang = getLang()) {
  const last = [...chatHistory].reverse().find((m) => m.role === "patient");
  const msg = last ? last.text : "";
  if (lang === "zh-CN") {
    return `请将下面患者情况整理为一条安抚且务实的短信（80字以内）：
"${msg}"`;
  }
  return `아래 환자 상황을 80자 내외의 안심 문자로 정리해 주세요:
"${msg}"`;
}
export function buildMemoPrompt(chatHistory, lang = getLang()) {
  const transcript = chatHistory
    .map((m) => (m.role === "user" ? `스태프: ${m.text}` : `환자: ${m.text}`))
    .join("\n");
  if (lang === "zh-CN") {
    return `请基于下述对话撰写一份简短院内备忘，以便院长复核：
${transcript}`;
  }
  return `아래 대화를 바탕으로 원장님 열람용 짧은 내원 메모를 작성해 주세요:
${transcript}`;
}

/** ───────── 강건 JSON 파서(추천 멘트용) ───────── */
function parseOptionsLoose(raw) {
  if (!raw) return [];
  let txt = String(raw).trim();
  txt = txt.replace(/```(?:json)?/gi, "").replace(/```/g, "").trim();
  const m = txt.match(/\{[\s\S]*\}/);
  const candidate = m ? m[0] : txt;
  try {
    const j = JSON.parse(candidate);
    const arr = Array.isArray(j?.options) ? j.options : [];
    return arr
      .filter((o) => o && typeof o.text === "string" && o.text.trim())
      .map((o, i) => ({
        strategy: o.strategy || ["공감", "정보수집", "해결"][i] || "추천",
        text: o.text.trim(),
      }));
  } catch {
    return [];
  }
}

/** ───────── 최종 평가 파서 ───────── */
function parseEvaluation(answer) {
  const txt = String(answer).replace(/\r\n/g, "\n");

  const grab = (labelRe) => {
    const start = txt.search(labelRe);
    if (start < 0) return "";
    const rest = txt.slice(start);
    const next = rest.search(/\n###\s/);
    return (next >= 0 ? rest.slice(0, next) : rest).trim();
  };

  const secScore = grab(/###\s*(점수|分数)/);
  const secSummary = grab(/###\s*(요약|总结)/);
  const secStrengths = grab(/###\s*(강점|优势)/);
  const secImprovements = grab(/###\s*(개선\s*포인트|改进点)/);
  const secNext = grab(/###\s*(다음\s*통화를\s*위한\s*코칭\s*팁|下一次通话建议)/);

  const scoreMatch = secScore.match(/(\d{1,3})/);
  const score = scoreMatch ? Number(scoreMatch[1]) : null;

  const linesToList = (block) =>
    block
      .split("\n")
      .map((l) => l.replace(/^###.*$/, "").trim())
      .filter((l) => /^- /.test(l))
      .map((l) => {
        // 불릿 제거
        let s = l.replace(/^- /, "").trim();
        // **굵은 라벨**: 또는 **굵은 라벨** 형태 제거
        s = s.replace(/^\*\*[^*]+?\*\*:?[\s\u00A0]*/,'');
        // 혹시 남은 ** 마크다운 제거
        s = s.replace(/\*\*(.*?)\*\*/g, "$1");
        return s.trim();
      })
      .filter(Boolean);

  const summary = secSummary
    .split("\n")
    .map((l) => l.trim())
    .filter((l) => l && !l.startsWith("###"))[0] || "";

  return {
    score,
    summary,
    strengths: linesToList(secStrengths),
    improvements: linesToList(secImprovements),
    nextTips: linesToList(secNext),
    raw: answer,
  };
}

/** ───────── API 래퍼 ───────── */
export const SimulationAI = {
  async init() {
    const user = getUserOrDie();
    const contents = [{ role: "user", parts: [{ text: buildInitPrompt() }] }];
    const data = await callAi({
      contents,
      api_name: "simulation",
      userId: user.id,
      generationConfig: null,
      version: undefined,
    });
    const answer =
      data?.data?.answer ?? data?.answer ?? data?.data?.text ?? data?.text ?? "";
    if (!answer) throw new Error("초기 멘트 생성 실패");
    return answer;
  },

  async reply(chatHistory) {
    const user = getUserOrDie();
    const prompt = buildReplyPrompt(chatHistory);
    const contents = [{ role: "user", parts: [{ text: prompt }] }];
    const data = await callAi({
      contents,
      api_name: "simulation",
      userId: user.id,
      generationConfig: null,
      version: undefined,
    });
    const answer =
      data?.data?.answer ?? data?.answer ?? data?.data?.text ?? data?.text ?? "";
    if (!answer) throw new Error("응답 생성 실패");
    return answer;
  },

  async coaching(staffText, patientText = "") {
    const user = getUserOrDie();  
    const lang =
      (typeof document !== "undefined" && document.documentElement?.lang) || "ko";

    const prompt = buildCoachingPrompt(lang, staffText, patientText);
    const contents = [{ role: "user", parts: [{ text: prompt }] }];

    const data = await callAi({
      contents,
      api_name: "simulation:coaching",
      userId: user.id,                
      generationConfig: { temperature: 0.7 },
      version: undefined,
    });

    const answer =
      data?.data?.answer ?? data?.answer ?? data?.data?.text ?? data?.text ?? "";
    return String(answer).trim();
  },

  async recommendations(lastPatientUtterance) {
    const user = getUserOrDie();
    const prompt = buildRecommendationPrompt(lastPatientUtterance);
    const contents = [{ role: "user", parts: [{ text: prompt }] }];
    const data = await callAi({
      contents,
      api_name: "simulation",
      userId: user.id,
      generationConfig: null,
      version: undefined,
    });
    const raw =
      data?.data?.answer ?? data?.answer ?? data?.data?.text ?? data?.text ?? "";
    const opts = parseOptionsLoose(raw);
    return opts;
  },

  async evaluate(chatHistory) {
    const user = getUserOrDie();
    const prompt = buildEvaluationPrompt(chatHistory);
    const contents = [{ role: "user", parts: [{ text: prompt }] }];
    const data = await callAi({
      contents,
      api_name: "simulation",
      userId: user.id,
      generationConfig: null,
      version: undefined,
    });
    const answer =
      data?.data?.answer ?? data?.answer ?? data?.data?.text ?? data?.text ?? "";
    return parseEvaluation(answer || "");
  },

  async sms(chatHistory) {
    const user = getUserOrDie();
    const prompt = buildSmsPrompt(chatHistory);
    const contents = [{ role: "user", parts: [{ text: prompt }] }];
    const data = await callAi({
      contents,
      api_name: "simulation",
      userId: user.id,
      generationConfig: null,
      version: undefined,
    });
    const answer =
      data?.data?.answer ?? data?.answer ?? data?.data?.text ?? data?.text ?? "";
    return answer || "";
  },

  async memo(chatHistory) {
    const user = getUserOrDie();
    const prompt = buildMemoPrompt(chatHistory);
    const contents = [{ role: "user", parts: [{ text: prompt }] }];
    const data = await callAi({
      contents,
      api_name: "simulation",
      userId: user.id,
      generationConfig: null,
      version: undefined,
    });
    const answer =
      data?.data?.answer ?? data?.answer ?? data?.data?.text ?? data?.text ?? "";
    return answer || "";
  },
};
